<script setup lang="ts">
import { computed } from 'vue'
import { useNotificationStore } from '@/stores/notificationStore'
import NotificationCard from '@/components/NotificationCard.vue'

/**
 * PrimeVue `<Toast />` host. Renders our shared `<NotificationCard />`
 * inside PrimeVue's `#container` slot so we get PrimeVue's overlay
 * engine + positioning + enter/leave animations while keeping the
 * card chrome identical to the native driver.
 *
 * ## Scoping to the main content region
 *
 * PrimeVue v4's `<Toast>` wraps its overlay in an internal `<Portal>`
 * that's instantiated without props — so `appendTo` defaults to
 * `'body'` and there's no public API to override that. The toast
 * ALWAYS teleports to body.
 *
 * Workaround (in `main.scss` + `default.vue`): JS publishes
 * `<main>`'s bounding rect as `--app-main-*` CSS custom properties
 * on `:root`; a global CSS rule re-anchors the per-position
 * `.p-toast-{position}` classes so the corner offsets become
 * relative to main's bounds instead of the viewport. PrimeVue still
 * thinks it's positioning against the viewport — visually the
 * overlay sits inside `<main>`, never overlapping header / sidebar
 * / footer. See `main.scss` § "PrimeVue Toast — main-bound anchor".
 *
 * ## Lifecycle ownership stays with the store
 *   - `add` is emitted by the store on `alertX()` calls with the
 *     entry's UUID stashed in `message.data.notificationId`.
 *   - `life: 0` disables PrimeVue's own auto-dismiss — the store's
 *     `setTimeout` is the SINGLE source of dismissal so pause-on-
 *     hover behaves identically across both drivers.
 *   - Manual dismiss (X) calls `closeCallback` so PrimeVue's wrapper
 *     unmounts cleanly.
 *   - Auto-dismiss emits `'remove'` on the bus WITH the original
 *     message reference (see `pvMessageRefs` in the store) so
 *     PrimeVue's reference-equality match removes the wrapper.
 *     Without this the `.p-toast-message` wrapper leaks.
 *
 * Position re-mount key — PrimeVue's `<Toast position>` prop is
 * read once at mount, so a position change requires re-creating
 * the component. The keyed re-render handles it automatically.
 */
const store = useNotificationStore()

const positionKey = computed(() => `pv-toast-${store.position}`)
</script>

<template>
  <Toast :key="positionKey" :position="store.position">
    <template #container="{ message, closeCallback }">
      <NotificationCard
        v-if="store.notifications.get(message?.data?.notificationId)"
        :entry="store.notifications.get(message?.data?.notificationId)!"
        :on-after-dismiss="closeCallback"
      />
    </template>
  </Toast>
</template>
